import styled from "styled-components";
import { Button } from "@mui/material";
import { palette, typography } from "@src/core/styles";

export const JsonValidatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1400px;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const IndentLabel = styled.span`
  ${typography.body_medium_regular};
  color: ${palette.colors.onsurface_variant};
  white-space: nowrap;
`;

export const IndentSelector = styled.select`
  ${typography.body_medium_regular};
  padding: 6px 10px;
  border: 1px solid ${palette.colors.outline_variant};
  border-radius: 8px;
  background-color: ${palette.colors.surface};
  color: ${palette.colors.onsurface};
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${palette.colors.green_base};
  }
`;

export const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${palette.colors.outline_variant};
  border-radius: 12px;
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: ${palette.colors.surface_container};
  border-bottom: 1px solid ${palette.colors.outline_variant};
  min-height: 44px;
`;

export const PanelTitle = styled.span`
  ${typography.body_medium_regular};
  font-weight: 500;
  color: ${palette.colors.onsurface_variant};
`;

export const PanelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ActionButton = styled(Button)`
  && {
    ${typography.body_large_medium};
    background-color: ${palette.colors.green_base};
    color: ${palette.colors.green10};
    padding: 10px 20px;
    border-radius: 8px;
    text-transform: none;
    min-width: 130px;

    &:hover {
      background-color: ${palette.colors.button_hover};
    }

    &:disabled {
      background-color: ${palette.colors.outline_variant};
      color: ${palette.colors.onsurface_variant};
    }

    &.MuiButton-outlined {
      background-color: transparent;
      border-color: ${palette.colors.green_base};
      color: ${palette.colors.green_base};

      &:hover {
        background-color: ${palette.colors.surface_container};
        border-color: ${palette.colors.green60};
      }

      &:disabled {
        border-color: ${palette.colors.outline_variant};
        color: ${palette.colors.onsurface_variant};
      }
    }
  }
`;

export const ErrorMessage = styled.div`
  ${typography.body_medium_regular};
  color: ${palette.colors.error};
  padding: 10px 14px;
  background-color: ${palette.colors.surface};
  border-top: 1px solid ${palette.colors.error};
  font-family: "Courier New", monospace;
  font-size: 13px;
`;

export const SuccessMessage = styled.div`
  ${typography.body_medium_regular};
  color: ${palette.colors.green_base};
  padding: 10px 14px;
  background-color: ${palette.colors.secondary_container};
  border-top: 1px solid ${palette.colors.green_base};
`;

export const CopyButton = styled(Button)`
  && {
    ${typography.body_medium_regular};
    font-size: 13px;
    border-color: ${palette.colors.outline_variant};
    color: ${palette.colors.onsurface_variant};
    text-transform: none;
    padding: 4px 10px;
    min-width: auto;

    &:hover {
      background-color: ${palette.colors.surface_container};
      border-color: ${palette.colors.green_base};
      color: ${palette.colors.green_base};
    }
  }
`;

export const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 14px 20px;
  background-color: ${palette.colors.surface_container};
  border-radius: 10px;
  border: 1px solid ${palette.colors.outline_variant};
  flex-wrap: wrap;
`;

export const StatItem = styled.div`
  ${typography.body_medium_regular};
  color: ${palette.colors.onsurface_variant};

  strong {
    color: ${palette.colors.onsurface};
    margin-right: 6px;
  }
`;

export const CodeMirrorWrapper = styled.div<{ $hasError?: boolean; $hasSuccess?: boolean }>`
  width: 100%;
  border-top: none;
  flex: 1;

  .cm-editor {
    font-size: 14px;
    font-family: "JetBrains Mono", "Fira Code", "Courier New", "Monaco", monospace;
    background-color: ${palette.colors.surface};
    border-top: 2px solid
      ${(props) => {
        if (props.$hasError) return palette.colors.error;
        if (props.$hasSuccess) return palette.colors.green_base;
        return "transparent";
      }};
    transition: border-color 0.2s ease;
  }

  .cm-content {
    font-family: "JetBrains Mono", "Fira Code", "Courier New", "Monaco", monospace;
  }

  .cm-gutters {
    background-color: ${palette.colors.surface_container};
    border-right: 1px solid ${palette.colors.outline_variant};
    color: ${palette.colors.onsurface_variant};
    font-family: "JetBrains Mono", "Fira Code", "Courier New", monospace;
  }

  .cm-activeLineGutter {
    background-color: ${palette.colors.secondary_container};
  }

  .cm-line {
    padding: 0 4px;
  }

  .cm-scroller {
    overflow: auto;
  }

  .cm-focused {
    outline: none !important;
  }
`;
